import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiEdit2Fill } from 'react-icons/ri';
import { IoCubeOutline, IoLocationOutline, IoLanguage, IoPower } from 'react-icons/io5';
import { HiOutlineLockClosed } from 'react-icons/hi2';
import SpinnerIcon from '../../assets/spinner.svg';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ChangePassword from './ChangePassword';
import EditProfile from './EditProfile';
import { useUser } from '../../contexts/user';
import { DOCUMENTATION_URL } from '../../config/appConfig';
import { useCart } from '../../contexts/cart';
import useToast from '../../hooks/useToast';
import log from '../../utils/logger';
import api from '../../api/api';
import Toast from '../Toast';


const ProfileSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user, idToken, logout } = useUser();
  const { clearCart } = useCart();
  const { toastMessage, toastType, showToast } = useToast();
  const navigate = useNavigate();

  const handleChangePasswordModal = () => {
    setIsChangePasswordModalOpen(!isChangePasswordModalOpen);
  };

  const handlePasswordUpdateSuccess = () => {
    showToast(t('passwordUpdateSuccess'), 'success');
  };

  const handleEditProfileModal = () => {
    setIsEditProfileModalOpen(!isEditProfileModalOpen);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    if (!user || !idToken) {
      log.warn('No user logged in');
      navigate('/home');
      logout();
      clearCart();
      return;
    }

    try {
      await api.post(
        '/user/logout',
        {},
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      );

      logout();
      clearCart();
      navigate('/home');
    } catch (error) {
      log.error('Logout error:', error?.response?.data || error.message);
      logout();
      clearCart();
      setIsLoggingOut(false);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className='w-full lg:w-[320px]'>

      {/* User details  */}
      <div className='flex items-end justify-between gap-2 lg:relative sticky top-14 lg:top-auto bg-white py-4 lg:py-0 px-5 lg:px-0 lg:border-b-0 border-b border-gray-300 left-0 right-0 w-full z-10'>
        <div>
          <h4 className='font-semibold text-lg'>{user?.firstName} {user?.lastName}</h4>
          <p className='font-medium text-sm text-[#6D717B]'>{user?.phoneNumber?.countryCode}{user?.phoneNumber?.number}</p>
          <p className='font-medium text-sm text-[#6D717B]'>{user?.email}</p>
        </div>

        <div>
          <button onClick={handleEditProfileModal} className='w-[35px] h-[35px] bg-gray-300 rounded-lg flex items-center justify-center'>
            <RiEdit2Fill className='text-xl' />
          </button>

          {isEditProfileModalOpen && (
            <EditProfile onClose={handleEditProfileModal} isEditProfileModalOpen={isEditProfileModalOpen} />
          )}
        </div>
      </div>

      {/* Links  */}

      <ul className='flex flex-col gap-4 px-2 mt-6 lg:mt-16'>
        <li className='w-full'>
          <Link to="/profile/orders" className={`py-1 px-2 lg:px-4 flex items-center gap-4 lg:gap-8 w-full text-[13px] font-semibold h-11 rounded-lg transition-all duration-300 ${pathname === '/profile/orders' ? 'bg-[#04B7DF] opacity-90 text-white' : 'hover:bg-[#eee] text-black'}`}>
            <IoCubeOutline className='w-6 h-6' />
            {t('My Orders')}
          </Link>
        </li>

        <li className='w-full'>
          <Link to="/profile/manage-address" className={`py-1 px-2 lg:px-4 flex items-center gap-4 lg:gap-8 w-full text-[13px] font-semibold h-11 rounded-lg transition-all duration-300 ${pathname === '/profile/manage-address' ? 'bg-[#04B7DF] opacity-90 text-white' : 'hover:bg-[#eee] text-black'}`}>
            <IoLocationOutline className='w-6 h-6' />
            {t('Manage addresses')}
          </Link>
        </li>

        <li className='w-full'>
          <Link to="/profile/change-language" className={`py-1 px-2 lg:px-4 flex items-center gap-4 lg:gap-8 w-full text-[13px] font-semibold h-11 rounded-lg transition-all duration-300 ${pathname === '/profile/change-language' ? 'bg-[#04B7DF] opacity-90 text-white' : 'hover:bg-[#eee] text-black'}`}>
            <IoLanguage className='w-6 h-6' />
            {t('Change language')}
          </Link>
        </li>

        <li className='w-full'>
          <button onClick={handleChangePasswordModal} className={'py-1 px-2 lg:px-4 flex items-center gap-4 lg:gap-8 w-full text-[13px] font-semibold h-11 rounded-lg transition-all duration-300 hover:bg-[#eee] text-black'}>
            <HiOutlineLockClosed className='w-6 h-6' />
            {t('Change Password')}
          </button>

          {isChangePasswordModalOpen && (
            <ChangePassword
              onClose={handleChangePasswordModal}
              onPasswordUpdateSuccess={handlePasswordUpdateSuccess}
              isChangePasswordModalOpen={isChangePasswordModalOpen}
            />
          )}
        </li>

        <li className="w-full">
          <button
            onClick={handleLogout}
            className="py-1 px-2 lg:px-4 flex items-center gap-4 lg:gap-8 w-full text-[13px] font-semibold h-11 rounded-lg transition-all duration-300 hover:bg-[#eee] text-black"
            type="button"
          >
            {isLoggingOut ? (
              <img
                src={SpinnerIcon}
                alt="Loading..."
                className="h-5 w-5 animate-spin mx-auto"
              />
            ) : (
              <>
                <IoPower className="w-6 h-6" />
                {t('Log Out')}
              </>
            )}
          </button>
        </li>
      </ul>

      {/* Mobile-only Links Navigation */}
      <div
        className={'lg:hidden'}
      >
        <ul className='flex flex-col px-2 mt-[20px]'>
          <button
            className='py-1 px-2 lg:px-4 flex items-center gap-4 lg:gap-8 w-full text-[13px] font-semibold h-11 rounded-lg transition-all duration-300 hover:bg-[#eee] text-black'
            onClick={() => navigate('/home')}
          >
            {t('home2')}
          </button>
          <button
            onClick={() => window.open(DOCUMENTATION_URL, '_blank')}
            className='py-1 px-2 lg:px-4 flex items-center gap-4 lg:gap-8 w-full text-[13px] font-semibold h-11 rounded-lg transition-all duration-300 hover:bg-[#eee] text-black'
          >
            {t('Documentation')}
          </button>
          <button
            onClick={() => navigate('/terms-of-use')}
            className='py-1 px-2 lg:px-4 flex items-center gap-4 lg:gap-8 w-full text-[13px] font-semibold h-11 rounded-lg transition-all duration-300 hover:bg-[#eee] text-black'
          >
            {t('Terms of Use')}
          </button>
        </ul>
      </div>
      <Toast message={toastMessage} type={toastType} />
    </div>
  );
};

export default ProfileSidebar;